import type {
  CertifiedCheckpoint,
  CheckpointAcknowledgment,
  ElectionCertificate,
  ElectionRequest,
  ElectionVote,
  RecoveryCheckpoint,
  RecoveryHeartbeat,
  RecoveryInputRecord,
  RecoveryStatus,
} from "./recovery-types.js";
import {
  electionKey,
  majority,
  orderCheckpoint,
  positive,
  repeatedElection,
  safeTime,
  same,
  token,
  trimRecoveryMap,
  validateCertified,
  validateCheckpoint,
  validateElectionCertificate,
  validateElectionRequest,
  validateElectionVote,
  validateHeartbeat,
  voterSet,
  voterSubset,
} from "./recovery-validation.js";

export * from "./recovery-types.js";

export const RECOVERY_CHECKPOINT_INTERVAL_MS = 5_000;
export const RECOVERY_HEARTBEAT_TIMEOUT_MS = 3_000;
export const RECOVERY_CERTIFIED_RETENTION = 2;
export const RECOVERY_INPUT_RETENTION = 4_096;
export const RECOVERY_AGREEMENT_RETENTION = 32;
export const RECOVERY_PACKET_MAX_BYTES = 4_194_304;

export class RecoveryStateMachine {
  readonly #selfId: string;
  #authorityEpoch: number;
  #term = 0;
  #hostMemberId: string;
  #membershipVersion: number;
  #voters: readonly string[];
  #lastHeartbeatWallMs: number;
  #candidate: RecoveryCheckpoint | undefined;
  #certified: CertifiedCheckpoint[] = [];
  #acceptedInputs: RecoveryInputRecord[] = [];
  readonly #checkpointAcks = new Map<string, Set<string>>();
  readonly #electionVotes = new Map<string, Set<string>>();
  readonly #votesByTerm = new Map<number, string>();
  readonly #requestsByTerm = new Map<number, ElectionRequest>();

  constructor(options: {
    readonly selfId: string;
    readonly hostMemberId: string;
    readonly voters: readonly string[];
    readonly authorityEpoch?: number;
    readonly membershipVersion?: number;
    readonly nowWallMs?: number;
  }) {
    this.#selfId = token(options.selfId, "local member ID");
    this.#hostMemberId = token(options.hostMemberId, "host member ID");
    this.#authorityEpoch = positive(options.authorityEpoch ?? 1, "authority epoch");
    this.#membershipVersion = positive(options.membershipVersion ?? 1, "membership version");
    this.#voters = voterSet(options.voters);
    if (!this.#voters.includes(this.#hostMemberId))
      throw new TypeError("Recovery host must belong to the voter set");
    this.#lastHeartbeatWallMs = safeTime(options.nowWallMs ?? 0, "heartbeat wall time");
  }

  status(nowWallMs: number): RecoveryStatus {
    const status = {
      authorityEpoch: this.#authorityEpoch,
      term: this.#term,
      hostMemberId: this.#hostMemberId,
      membershipVersion: this.#membershipVersion,
      voters: this.#voters,
      frozen: this.frozen(nowWallMs),
      certified: Object.freeze(structuredClone(this.#certified)),
      ...(this.#candidate ? { candidate: structuredClone(this.#candidate) } : {}),
      acceptedInputs: Object.freeze(structuredClone(this.#acceptedInputs)),
      retainedAgreementRecords:
        this.#checkpointAcks.size +
        this.#electionVotes.size +
        this.#votesByTerm.size +
        this.#requestsByTerm.size,
    };
    return Object.freeze(status);
  }

  heartbeat(gameTimeMs: number): RecoveryHeartbeat {
    return Object.freeze({
      authorityEpoch: this.#authorityEpoch,
      hostMemberId: this.#hostMemberId,
      term: this.#term,
      gameTimeMs: safeTime(gameTimeMs, "heartbeat game time"),
    });
  }

  observeHeartbeat(message: RecoveryHeartbeat, nowWallMs: number): boolean {
    safeTime(nowWallMs, "heartbeat wall time");
    validateHeartbeat(message);
    if (
      message.authorityEpoch !== this.#authorityEpoch ||
      message.hostMemberId !== this.#hostMemberId ||
      message.term < this.#term
    )
      return false;
    this.#term = message.term;
    this.#lastHeartbeatWallMs = Math.max(this.#lastHeartbeatWallMs, nowWallMs);
    return true;
  }

  frozen(nowWallMs: number): boolean {
    return (
      safeTime(nowWallMs, "recovery wall time") - this.#lastHeartbeatWallMs >=
      RECOVERY_HEARTBEAT_TIMEOUT_MS
    );
  }

  offerCheckpoint(checkpoint: RecoveryCheckpoint): CheckpointAcknowledgment {
    validateCheckpoint(checkpoint);
    if (
      checkpoint.authorityEpoch !== this.#authorityEpoch ||
      checkpoint.membershipVersion !== this.#membershipVersion ||
      checkpoint.hostMemberId !== this.#hostMemberId ||
      !same(checkpoint.voters, this.#voters)
    )
      throw new TypeError("Checkpoint does not match current recovery authority");
    const newest = this.newestCheckpoint();
    if (!newest || orderCheckpoint(checkpoint, newest) >= 0)
      this.#candidate = structuredClone(checkpoint);
    return Object.freeze({
      checkpointId: checkpoint.id,
      authorityEpoch: checkpoint.authorityEpoch,
      membershipVersion: checkpoint.membershipVersion,
      memberId: this.#selfId,
    });
  }

  recordCheckpointAcknowledgment(
    acknowledgment: CheckpointAcknowledgment,
  ): CertifiedCheckpoint | undefined {
    const checkpoint = this.#candidate;
    if (
      !checkpoint ||
      acknowledgment.checkpointId !== checkpoint.id ||
      acknowledgment.authorityEpoch !== this.#authorityEpoch ||
      acknowledgment.membershipVersion !== this.#membershipVersion ||
      !this.#voters.includes(acknowledgment.memberId)
    )
      return undefined;
    const acknowledgers = this.#checkpointAcks.get(checkpoint.id) ?? new Set<string>();
    acknowledgers.add(acknowledgment.memberId);
    this.#checkpointAcks.set(checkpoint.id, acknowledgers);
    trimRecoveryMap(this.#checkpointAcks, RECOVERY_AGREEMENT_RETENTION);
    if (!majority(acknowledgers.size, this.#voters.length)) return undefined;
    const certified = Object.freeze({
      checkpoint: structuredClone(checkpoint),
      certificate: Object.freeze({
        checkpointId: checkpoint.id,
        authorityEpoch: checkpoint.authorityEpoch,
        membershipVersion: checkpoint.membershipVersion,
        acknowledgers: Object.freeze([...acknowledgers].sort()),
      }),
    });
    this.installCertifiedCheckpoint(certified);
    return certified;
  }

  installCertifiedCheckpoint(bundle: CertifiedCheckpoint): void {
    validateCertified(bundle, this.#voters);
    if (
      bundle.checkpoint.authorityEpoch !== this.#authorityEpoch ||
      bundle.checkpoint.membershipVersion !== this.#membershipVersion ||
      bundle.checkpoint.hostMemberId !== this.#hostMemberId ||
      !same(bundle.checkpoint.voters, this.#voters)
    )
      throw new TypeError("Certified checkpoint does not match current recovery authority");
    const byId = new Map(this.#certified.map((entry) => [entry.checkpoint.id, entry]));
    byId.set(bundle.checkpoint.id, structuredClone(bundle));
    this.#certified = [...byId.values()]
      .sort((left, right) => orderCheckpoint(left.checkpoint, right.checkpoint))
      .slice(-RECOVERY_CERTIFIED_RETENTION);
    if (this.#candidate?.id === bundle.checkpoint.id) this.#candidate = undefined;
    this.pruneInputs();
  }

  recordAcceptedInput(record: RecoveryInputRecord): void {
    safeTime(record.gameTimeMs, "accepted input game time");
    if (record.entry.kind !== "accepted-input") throw new TypeError("Invalid recovery input");
    this.#acceptedInputs.push(structuredClone(record));
    this.pruneInputs();
  }

  beginElection(
    nowWallMs: number,
    connectedVoters: readonly string[],
  ): ElectionRequest | undefined {
    if (!this.frozen(nowWallMs)) return undefined;
    const connected = voterSubset(connectedVoters, this.#voters);
    if (!connected.includes(this.#selfId) || !majority(connected.length, this.#voters.length))
      return undefined;
    const candidateId = connected[0] as string;
    if (candidateId !== this.#selfId) return undefined;
    const checkpoint = this.#certified.at(-1);
    if (!checkpoint) return undefined;
    this.#term += 1;
    const request = Object.freeze({
      term: this.#term,
      priorAuthorityEpoch: this.#authorityEpoch,
      candidateId,
      connectedVoters: connected,
      checkpointId: checkpoint.checkpoint.id,
    });
    this.#requestsByTerm.set(request.term, request);
    trimRecoveryMap(this.#requestsByTerm, RECOVERY_AGREEMENT_RETENTION);
    return request;
  }

  vote(request: ElectionRequest): ElectionVote | undefined {
    validateElectionRequest(request);
    if (
      request.priorAuthorityEpoch !== this.#authorityEpoch ||
      !this.#voters.includes(this.#selfId)
    )
      return undefined;
    const connected = voterSubset(request.connectedVoters, this.#voters);
    if (
      !same(connected, request.connectedVoters) ||
      !majority(connected.length, this.#voters.length) ||
      connected[0] !== request.candidateId ||
      !connected.includes(this.#selfId) ||
      !this.#certified.some((entry) => entry.checkpoint.id === request.checkpointId)
    )
      return undefined;
    const signature = electionKey(request);
    const prior = this.#votesByTerm.get(request.term);
    if (request.term < this.#term || (prior && prior !== signature)) return undefined;
    this.#term = Math.max(this.#term, request.term);
    this.#votesByTerm.set(request.term, signature);
    return Object.freeze({
      term: request.term,
      priorAuthorityEpoch: request.priorAuthorityEpoch,
      candidateId: request.candidateId,
      checkpointId: request.checkpointId,
      voterId: this.#selfId,
    });
  }

  recordElectionVote(vote: ElectionVote): ElectionCertificate | undefined {
    validateElectionVote(vote);
    if (
      vote.priorAuthorityEpoch !== this.#authorityEpoch ||
      vote.candidateId !== this.#selfId ||
      vote.term !== this.#term ||
      !this.#voters.includes(vote.voterId) ||
      !this.#certified.some((entry) => entry.checkpoint.id === vote.checkpointId) ||
      this.#requestsByTerm.get(vote.term)?.checkpointId !== vote.checkpointId
    )
      return undefined;
    const key = `${vote.term}:${vote.priorAuthorityEpoch}:${vote.candidateId}:${vote.checkpointId}`;
    const voters = this.#electionVotes.get(key) ?? new Set<string>();
    voters.add(vote.voterId);
    this.#electionVotes.set(key, voters);
    trimRecoveryMap(this.#electionVotes, RECOVERY_AGREEMENT_RETENTION);
    if (!majority(voters.size, this.#voters.length)) return undefined;
    return Object.freeze({
      term: vote.term,
      priorAuthorityEpoch: this.#authorityEpoch,
      authorityEpoch: this.#authorityEpoch + 1,
      hostMemberId: this.#selfId,
      checkpointId: vote.checkpointId,
      voters: Object.freeze([...voters].sort()),
    });
  }

  installElection(certificate: ElectionCertificate, nowWallMs: number): CertifiedCheckpoint {
    validateElectionCertificate(certificate, this.#voters);
    const repeated = repeatedElection(certificate, this.status(nowWallMs), this.#certified);
    if (repeated) return repeated;
    if (
      certificate.priorAuthorityEpoch !== this.#authorityEpoch ||
      certificate.authorityEpoch !== this.#authorityEpoch + 1 ||
      certificate.term < this.#term
    )
      throw new TypeError("Election certificate is stale or incompatible");
    const checkpoint = this.#certified.find(
      (entry) => entry.checkpoint.id === certificate.checkpointId,
    );
    if (!checkpoint) throw new TypeError("Election certificate names an unavailable checkpoint");
    this.#authorityEpoch = certificate.authorityEpoch;
    this.#term = certificate.term;
    this.#hostMemberId = certificate.hostMemberId;
    this.#lastHeartbeatWallMs = safeTime(nowWallMs, "election wall time");
    this.#candidate = undefined;
    this.#electionVotes.clear();
    this.#requestsByTerm.clear();
    for (const term of this.#votesByTerm.keys()) {
      if (term < this.#term) this.#votesByTerm.delete(term);
    }
    return structuredClone(checkpoint);
  }

  prepareHandoff(nextHostMemberId: string): ElectionCertificate {
    const host = token(nextHostMemberId, "handoff host member ID");
    if (this.#selfId !== this.#hostMemberId)
      throw new TypeError("Only the current host can hand off authority");
    if (host === this.#hostMemberId || !this.#voters.includes(host))
      throw new TypeError("Handoff target must be another recovery voter");
    const certified = this.#certified.at(-1);
    if (!certified) throw new TypeError("Host handoff requires a certified checkpoint");
    this.#term += 1;
    return Object.freeze({
      term: this.#term,
      priorAuthorityEpoch: this.#authorityEpoch,
      authorityEpoch: this.#authorityEpoch + 1,
      hostMemberId: host,
      checkpointId: certified.checkpoint.id,
      voters: certified.certificate.acknowledgers,
    });
  }

  latestCertified(): CertifiedCheckpoint | undefined {
    const certified = this.#certified.at(-1);
    return certified ? structuredClone(certified) : undefined;
  }

  private newestCheckpoint(): RecoveryCheckpoint | undefined {
    const certified = this.#certified.at(-1)?.checkpoint;
    if (!this.#candidate) return certified;
    if (!certified || orderCheckpoint(this.#candidate, certified) >= 0) return this.#candidate;
    return certified;
  }

  private pruneInputs(): void {
    const floor = this.#certified.at(-RECOVERY_CERTIFIED_RETENTION)?.checkpoint.gameTimeMs ?? 0;
    this.#acceptedInputs = this.#acceptedInputs
      .filter((entry) => entry.gameTimeMs >= floor)
      .slice(-RECOVERY_INPUT_RETENTION);
  }
}
